import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { RotateDirection, SpecialZoomLevel } from "@react-pdf-viewer/core";

// Options for toolbarPlugin() in the restricted (no download / no print) viewer:
// turns off the plugin's Ctrl+P print shortcut.
export const RESTRICTED_TOOLBAR_OPTIONS = {
  printPlugin: { enableShortcuts: false },
};

const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];

// MUI tooltips render in a portal and flip/shift to stay inside the viewport,
// so they are never clipped by the dialog edge.
const tooltipSlotProps = {
  popper: {
    modifiers: [
      { name: "preventOverflow", options: { padding: 8, altAxis: true } },
    ],
  },
};

const ToolButton = ({ title, onClick, disabled = false, children }) => (
  <Tooltip title={title} placement="bottom" arrow slotProps={tooltipSlotProps}>
    {/* span keeps the tooltip working when the button is disabled */}
    <span style={{ display: "inline-flex" }}>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        aria-label={title}
        sx={{ width: 32, height: 32 }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const PageInput = ({ currentPage, numberOfPages, jumpToPage }) => {
  const [value, setValue] = useState(String(currentPage + 1));

  useEffect(() => {
    setValue(String(currentPage + 1));
  }, [currentPage]);

  const commit = () => {
    const page = parseInt(value, 10);
    if (page >= 1 && page <= numberOfPages) {
      jumpToPage(page - 1);
    } else {
      setValue(String(currentPage + 1));
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <TextField
        size="small"
        value={value}
        onChange={(e) => setValue(e.target.value.replace(/\D/g, ""))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
        slotProps={{
          htmlInput: {
            "aria-label": "Page number",
            inputMode: "numeric",
            style: {
              width: 36,
              padding: "4px 6px",
              textAlign: "center",
              fontSize: 14,
            },
          },
        }}
      />
      <Typography
        variant="body2"
        sx={{ whiteSpace: "nowrap", color: "text.secondary" }}
      >
        / {numberOfPages}
      </Typography>
    </Box>
  );
};

const GroupDivider = () => (
  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
);

// Page navigation, zoom, rotate and fullscreen — no search, download or print.
const PdfViewerToolbar = ({ toolbarPluginInstance }) => {
  const { Toolbar, pageNavigationPluginInstance } = toolbarPluginInstance;

  return (
    <Toolbar>
      {(slots) => {
        const {
          CurrentPageLabel,
          GoToNextPage,
          GoToPreviousPage,
          Zoom,
          ZoomIn,
          ZoomOut,
          Rotate,
          EnterFullScreen,
        } = slots;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 1,
              px: 1.5,
              py: 0.75,
              minHeight: 48,
              borderBottom: "1px solid #e0e0e0",
              backgroundColor: "#fff",
            }}
          >
            <GoToPreviousPage>
              {(props) => (
                <ToolButton
                  title="Previous page"
                  onClick={props.onClick}
                  disabled={props.isDisabled}
                >
                  <KeyboardArrowUpIcon fontSize="small" />
                </ToolButton>
              )}
            </GoToPreviousPage>

            <CurrentPageLabel>
              {(props) => (
                <PageInput
                  currentPage={props.currentPage}
                  numberOfPages={props.numberOfPages}
                  jumpToPage={pageNavigationPluginInstance.jumpToPage}
                />
              )}
            </CurrentPageLabel>

            <GoToNextPage>
              {(props) => (
                <ToolButton
                  title="Next page"
                  onClick={props.onClick}
                  disabled={props.isDisabled}
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </ToolButton>
              )}
            </GoToNextPage>

            <GroupDivider />

            <ZoomOut>
              {(props) => (
                <ToolButton title="Zoom out" onClick={props.onClick}>
                  <ZoomOutIcon fontSize="small" />
                </ToolButton>
              )}
            </ZoomOut>

            <Zoom>
              {({ scale, onZoom }) => (
                <Select
                  size="small"
                  value=""
                  displayEmpty
                  renderValue={() => `${Math.round(scale * 100)}%`}
                  onChange={(e) => onZoom(e.target.value)}
                  inputProps={{ "aria-label": "Zoom level" }}
                  sx={{
                    minWidth: 88,
                    fontSize: 14,
                    "& .MuiSelect-select": { py: "4px" },
                  }}
                >
                  <MenuItem value={SpecialZoomLevel.ActualSize}>
                    Actual size
                  </MenuItem>
                  <MenuItem value={SpecialZoomLevel.PageFit}>Page fit</MenuItem>
                  <MenuItem value={SpecialZoomLevel.PageWidth}>
                    Page width
                  </MenuItem>
                  <Divider />
                  {ZOOM_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level * 100}%
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Zoom>

            <ZoomIn>
              {(props) => (
                <ToolButton title="Zoom in" onClick={props.onClick}>
                  <ZoomInIcon fontSize="small" />
                </ToolButton>
              )}
            </ZoomIn>

            <GroupDivider />

            <Rotate direction={RotateDirection.Forward}>
              {(props) => (
                <ToolButton title="Rotate clockwise" onClick={props.onClick}>
                  <RotateRightIcon fontSize="small" />
                </ToolButton>
              )}
            </Rotate>

            <EnterFullScreen>
              {(props) => (
                <ToolButton title="Full screen" onClick={props.onClick}>
                  <FullscreenIcon fontSize="small" />
                </ToolButton>
              )}
            </EnterFullScreen>
          </Box>
        );
      }}
    </Toolbar>
  );
};

export default PdfViewerToolbar;
